import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation } from '@tanstack/react-query'
import { X, Loader2, Copy, Link2 } from 'lucide-react'
import {
  curriculumBuilderApi,
  type AdminContent,
  type CreateDirectContentRequest,
} from '@/api/admin'
import { ContentPickerTree } from './ContentPickerTree'

const LEVEL_LABELS: Record<string, string> = {
  curriculum: '커리큘럼',
  course: '코스',
  module: '모듈',
}

interface CreateContentModalProps {
  level: 'curriculum' | 'course' | 'module'
  parentId?: number
  parentTitle?: string
  /** Children already attached to parent (for greying out in the picker). */
  existingChildIds?: Set<number>
  onClose: () => void
  onCreated: (content: AdminContent) => void
  /** Called after one or more attaches happen in the import tab. */
  onAttached?: (contents: AdminContent[]) => void
}

type Tab = 'new' | 'import'
type ImportMode = 'clone' | 'share'

export function CreateContentModal({
  level, parentId, parentTitle, existingChildIds, onClose, onCreated, onAttached,
}: CreateContentModalProps) {
  // curriculum-level has no parent, so import tab is not meaningful
  const canImport = level !== 'curriculum' && parentId != null
  const [tab, setTab] = useState<Tab>('new')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [pickedSet, setPickedSet] = useState<Set<number>>(new Set(existingChildIds ?? []))
  const [importMode, setImportMode] = useState<ImportMode>('clone')

  const createMutation = useMutation({
    mutationFn: (data: CreateDirectContentRequest) => curriculumBuilderApi.createContent(data),
    onSuccess: (content) => onCreated(content),
  })

  const cloneMutation = useMutation({
    mutationFn: (content: AdminContent) =>
      curriculumBuilderApi.cloneChildrenToParent(parentId!, [content.id]),
    onSuccess: (contents, content) => {
      setPickedSet((prev) => new Set([...prev, content.id]))
      onAttached?.(contents)
    },
  })

  const attachMutation = useMutation({
    mutationFn: (content: AdminContent) =>
      curriculumBuilderApi.attachChildrenToParent(parentId!, [content.id]),
    onSuccess: (contents) => {
      setPickedSet((prev) => {
        const next = new Set(prev)
        for (const c of contents) next.add(c.id)
        return next
      })
      onAttached?.(contents)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return
    createMutation.mutate({
      hierarchyLevel: level,
      title: title.trim(),
      description: description.trim() || undefined,
      parentId,
    })
  }

  const handlePick = (content: AdminContent) => {
    if (cloneMutation.isPending || attachMutation.isPending) return
    if (importMode === 'clone') {
      cloneMutation.mutate(content)
    } else {
      attachMutation.mutate(content)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-xl mx-4 max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <h2 className="text-lg font-semibold text-slate-900">
            {LEVEL_LABELS[level]} 추가
          </h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {parentTitle && (
          <div className="px-6 pt-3 text-sm text-slate-500 shrink-0">
            상위: <span className="font-medium text-slate-700">{parentTitle}</span>
          </div>
        )}

        {canImport && (
          <div className="px-6 pt-3 flex gap-1 shrink-0">
            <button
              type="button"
              onClick={() => setTab('new')}
              className={`px-3 py-1.5 text-sm rounded ${tab === 'new' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              새로 만들기
            </button>
            <button
              type="button"
              onClick={() => setTab('import')}
              className={`px-3 py-1.5 text-sm rounded ${tab === 'import' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              기존에서 가져오기
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {tab === 'new' || !canImport ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">제목 *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={`${LEVEL_LABELS[level]} 제목을 입력하세요`}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">설명</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="설명 (선택사항)"
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                />
              </div>
              {createMutation.isError && (
                <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                  생성에 실패했습니다. 다시 시도해주세요.
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={!title.trim() || createMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {createMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  만들기
                </button>
              </div>
            </form>
          ) : (
            <div>
              <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-2">
                <div className="grid grid-cols-2 gap-1">
                  <button
                    type="button"
                    onClick={() => setImportMode('clone')}
                    className={`flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition-colors ${
                      importMode === 'clone'
                        ? 'bg-white text-blue-700 shadow-sm'
                        : 'text-slate-600 hover:bg-white/70'
                    }`}
                  >
                    <Copy className="h-3.5 w-3.5" />
                    복제해서 가져오기
                  </button>
                  <button
                    type="button"
                    onClick={() => setImportMode('share')}
                    className={`flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition-colors ${
                      importMode === 'share'
                        ? 'bg-white text-blue-700 shadow-sm'
                        : 'text-slate-600 hover:bg-white/70'
                    }`}
                  >
                    <Link2 className="h-3.5 w-3.5" />
                    공유로 가져오기
                  </button>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  {importMode === 'clone'
                    ? '코스/모듈 구조는 새로 복제하고 레슨 콘텐츠만 재사용합니다.'
                    : '같은 코스/모듈을 공유합니다. 내부 레슨 변경은 모든 사용처에 반영됩니다.'}
                </p>
              </div>
              <ContentPickerTree
                childLevel={level as 'course' | 'module'}
                existingIds={pickedSet}
                onPick={handlePick}
              />
              {(cloneMutation.isError || attachMutation.isError) && (
                <div className="mt-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                  추가에 실패했습니다. 다시 시도해주세요.
                </div>
              )}
              <div className="flex justify-end pt-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  완료
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
